import { memo, useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'services/Translation'
import { statusText } from 'Constants'
import Typography from 'components/common/Typography'
import { primary, textPrimary, greySecondary } from 'styles/Colors'
import { buildStatusHistory } from './statusHistoryUtils'

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 260px;
  max-width: 320px;
  background: #fff;
  border: 1px solid ${greySecondary};
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
  padding: 10px 0;
  max-height: 240px;
  overflow-y: auto;
`

const DropdownHeader = styled.div`
  padding: 4px 14px 8px;
  border-bottom: 1px solid ${greySecondary};
  margin-bottom: 4px;
`

const HistoryItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 6px 14px;
  gap: 10px;

  &:hover {
    background: rgba(235, 124, 12, 0.05);
  }
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color || primary};
  margin-top: 4px;
  flex-shrink: 0;
`

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const texts = (getText) => (key) =>
  ({
    statusHistory: getText('Status History', 'StatusHistory'),
    noHistory: getText('No status history available', 'StatusHistory'),
  }[key])

const StatusHistoryTooltip = ({ summary, children }) => {
  const [open, setOpen] = useState(false)
  const timeoutRef = useRef(null)
  const { t } = useTranslation(texts)
  const { t: statusT } = useTranslation(statusText)
  const { formatDate: format } = useTranslation()

  const history = buildStatusHistory(summary)

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setOpen(true), 200)
  }, [])

  const handleMouseLeave = useCallback(() => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setOpen(false), 150)
  }, [])

  return (
    <Wrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {open && history.length > 0 && (
        <Dropdown>
          <DropdownHeader>
            <Typography text={t('statusHistory')} size={11} color={textPrimary} style={{ fontWeight: 600 }} />
          </DropdownHeader>
          {history.map((item, idx) => {
            const date = new Date(item.timestamp)
            return (
              <HistoryItem key={`${item.label}-${item.timestamp}-${idx}`}>
                <Dot color={item.color} />
                <ItemContent>
                  <Typography
                    text={statusT(item.label) || item.label}
                    size={11}
                    color={item.color}
                    style={{ fontWeight: 500 }}
                  />
                  <Typography
                    text={`${format(date, 'dd MMM yyyy')} · ${format(date, 'HH:mm')}`}
                    size={10}
                    color={textPrimary}
                    style={{ opacity: 0.6 }}
                  />
                </ItemContent>
              </HistoryItem>
            )
          })}
        </Dropdown>
      )}
    </Wrapper>
  )
}

export default memo(StatusHistoryTooltip)
